import { useCallback, useEffect, useRef, useState } from 'react';
import { writeStored, readStored } from '@/lib/storage';
import { parseList } from '@/lib/validation';
import type { StorageKey } from '@/lib/storage';

/**
 * A list that survives reloads.
 *
 * Seed content is used until the visitor's own data exists, and persisted data
 * is re-validated on read so a malformed or outdated payload degrades to the
 * seeds instead of crashing the view.
 */
export function usePersistentCollection<T>(
  key: StorageKey,
  seed: readonly T[],
  parseItem: (value: unknown) => T | null,
): {
  items: readonly T[];
  add: (item: T) => void;
  insertAt: (index: number, item: T) => void;
  remove: (predicate: (item: T) => boolean) => void;
  update: (predicate: (item: T) => boolean, updater: (item: T) => T) => void;
} {
  const [items, setItems] = useState<readonly T[]>(() => {
    const stored = readStored(key, (value) => parseList(value, parseItem));
    return stored ?? seed;
  });

  // Persist only once the list has actually changed. Comparing against the
  // initial reference — rather than flagging "the first effect run" — is what
  // makes this correct under StrictMode, whose mount/unmount/mount cycle runs
  // the effect twice and would otherwise write the seeds straight back out,
  // making them indistinguishable from content a visitor added.
  const initial = useRef(items);
  useEffect(() => {
    if (items === initial.current) return;
    writeStored(key, items);
  }, [key, items]);

  const add = useCallback((item: T) => {
    setItems((current) => [item, ...current]);
  }, []);

  /** Put an item back where it was — what undoing a deletion needs. */
  const insertAt = useCallback((index: number, item: T) => {
    setItems((current) => {
      const next = [...current];
      next.splice(Math.max(0, Math.min(index, next.length)), 0, item);
      return next;
    });
  }, []);

  const remove = useCallback((predicate: (item: T) => boolean) => {
    setItems((current) => current.filter((item) => !predicate(item)));
  }, []);

  const update = useCallback((predicate: (item: T) => boolean, updater: (item: T) => T) => {
    setItems((current) => current.map((item) => (predicate(item) ? updater(item) : item)));
  }, []);

  return { items, add, insertAt, remove, update };
}
